/**
 * Agenti (26/10/2026). Lorenzo: "le provvigioni agli agenti che ce li hanno
 * portati, tipo Cristian Taormina 10% sull'imponibile, Andrea Dovis stessa cosa,
 * e una sezione Agenti dove anche loro possono vedere".
 *
 * La domanda: quanto ha portato ogni agente e quanto gli spetta adesso?
 * La forma: a sinistra gli agenti (una scheda ciascuno, con quanto c'è da
 * pagare), a destra la scheda dell'agente scelto in parole, i suoi lavori con la
 * provvigione maturata (matura quando il cliente paga) e i pagamenti fatti.
 * Chi entra col ruolo Agente vede solo la sua scheda, senza poter scrivere.
 * L'agente di un progetto si sceglie nel progetto, scheda "Quanto ci resta".
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { Icon } from '../../components/ui/Icon';
import { Avatar } from '../../components/ui/Avatar';
import { SectionMark } from '../../components/ui/SectionMark';
import { BrandLogo } from '../../components/ui/BrandLogo';
import { useUrlState } from '../../hooks/useUrlState';
import { euro as euroIt } from '../../lib/utils';
import { leggibile } from '../../components/mobile/MobileKit';
import { nomeLavoro } from '../projects/ProgettiDesktop';
import './agenti.css';

interface Provvigione { agente: { id: string; nome: string } | null; pct: number; importo: number; maturata: number; pagata: number; daPagare: number }
interface SchedaAgente {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  commissionPct: number;
  active: boolean;
  note: string | null;
  user: { id: string; email: string; firstName: string; lastName: string; active: boolean } | null;
  clienti: Array<{ id: string; legalName: string; shortName: string | null }>;
  progetti: Array<{ id: string; code: string; name: string; status: string; client: { id: string; legalName: string; shortName: string | null }; imponibile: number; fatturato: number; incassato: number; provvigione: Provvigione }>;
  /** Fatture dei suoi clienti fuori dai progetti (abbonamenti GoDeal, canoni), una riga per cliente. */
  abbonamenti: Array<{ client: { id: string; legalName: string; shortName: string | null }; servizi: Servizio[]; fatture: number; dal: string; al: string; fatturato: number; incassato: number; provvigione: number; maturata: number; alMese: number | null }>;
  /** Gli abbonamenti insieme: i pagamenti senza un lavoro sono i loro. */
  abbonamentiTotali: { maturata: number; pagata: number; daPagare: number; alMese: number };
  pagamenti: Array<{ id: string; amount: number; paidAt: string; note: string | null; project: { id: string; code: string; name: string } | null }>;
  totali: { progetti: number; abbonamenti: number; imponibile: number; provvigione: number; maturata: number; pagata: number; daPagare: number; daMaturare: number };
}
interface Servizio { tipo: 'HOSTING' | 'GODEAL' | 'AURA_OS' | 'CUSTOM'; piano: string | null; importo: number; ciclo: 'MONTHLY' | 'QUARTERLY' | 'YEARLY'; dal: string | null }

const SERVIZIO: Record<Servizio['tipo'], string> = { GODEAL: 'GoDeal', HOSTING: 'Hosting', AURA_OS: 'Aura OS', CUSTOM: 'Servizio' };
const CICLO: Record<Servizio['ciclo'], string> = { MONTHLY: 'al mese', QUARTERLY: 'ogni tre mesi', YEARLY: "all'anno" };

// I centesimi quando ci sono, sotto i 10.000 €: una provvigione si paga al centesimo (8,11 al mese, non 8),
// il valore di un lavoro no.
const euro = (n: number) => euroIt(n, Math.abs(n) < 10_000 && Math.round(n * 100) % 100 !== 0);
const pct = (n: number) => `${String(n).replace('.', ',')}%`;
const oggi = () => new Date().toISOString().slice(0, 10);
const mese = (d: string) => new Date(d).toLocaleDateString('it-IT', { month: 'short', year: 'numeric' });
/** Le parole cambiano con chi guarda: noi paghiamo, l'agente riceve. */
const parole = (tu?: boolean) => (tu
  ? { pagata: 'ricevuta', pagati: 'ricevuti', daPagare: 'da ricevere', lui: 'a te', suoi: 'tuoi' }
  : { pagata: 'pagata', pagati: 'pagati', daPagare: 'da pagare', lui: 'a lui', suoi: 'suoi' });
/** "4 lavori e 3 abbonamenti". */
const quanti = (t: SchedaAgente['totali']) => [
  t.progetti ? (t.progetti === 1 ? '1 lavoro' : `${t.progetti} lavori`) : '',
  t.abbonamenti ? (t.abbonamenti === 1 ? '1 abbonamento' : `${t.abbonamenti} abbonamenti`) : '',
].filter(Boolean).join(' e ');

export function AgentiPage() {
  const ruolo = useAuthStore((s) => s.user?.role.key);
  return ruolo === 'AGENTE' ? <PortaleAgente /> : <GestioneAgenti />;
}

/** Quello che vede un agente: solo la sua scheda. */
function PortaleAgente() {
  const q = useQuery({ queryKey: ['agents', 'me'], queryFn: () => api.get<SchedaAgente>('/agents/me') });
  return (
    <div className="page ag-page">
      <header className="ag-hd">
        <div className="ag-hd-main">
          <SectionMark icon="handshake" className="ag-sez" />
          <div>
            <h1 className="ag-title">Le tue provvigioni</h1>
            {q.data && <p className="ag-risposta"><Frase a={q.data} tu /></p>}
            {q.isError && <p className="ag-risposta">Il tuo accesso non è ancora collegato a una scheda agente: scrivi a Masamune.</p>}
          </div>
        </div>
      </header>
      {q.data && <Dettaglio a={q.data} soloLettura />}
    </div>
  );
}

/** Quello che vede chi gestisce: tutti gli agenti. */
function GestioneAgenti() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['agents', 'tutti'], queryFn: () => api.get<SchedaAgente[]>('/agents') });
  const [scelto, setScelto] = useUrlState<string>('agente', '');
  const [nuovo, setNuovo] = useState(false);
  const agenti = q.data ?? [];
  // Senza una scelta si apre chi ha qualcosa da pagare, poi chi ha lavori.
  const a = agenti.find((x) => x.id === scelto)
    ?? agenti.find((x) => x.totali.daPagare > 0)
    ?? agenti.find((x) => x.totali.progetti + x.totali.abbonamenti > 0)
    ?? agenti[0] ?? null;

  const tot = agenti.reduce((s, x) => ({ imponibile: s.imponibile + x.totali.imponibile, daPagare: s.daPagare + x.totali.daPagare, maturata: s.maturata + x.totali.maturata }), { imponibile: 0, daPagare: 0, maturata: 0 });
  const conLavori = agenti.filter((x) => x.totali.progetti + x.totali.abbonamenti > 0).length;

  return (
    <div className="page ag-page">
      <header className="ag-hd">
        <div className="ag-hd-main">
          <SectionMark icon="handshake" className="ag-sez" />
          <div className="min-w-0">
            <h1 className="ag-title">Agenti</h1>
            {q.data && (
              <p className="ag-risposta">
                {agenti.length === 0 ? 'Nessun agente ancora.' : conLavori === 0
                  ? <>{agenti.length} agenti, ma nessun lavoro è ancora collegato a loro: l'agente si sceglie dentro ogni progetto, nella scheda <strong>Quanto ci resta</strong>.</>
                  : <>Gli agenti ci hanno portato lavori per <strong>{euro(tot.imponibile)}</strong> senza IVA. Provvigioni maturate <strong>{euro(tot.maturata)}</strong>, da pagare adesso <strong>{euro(tot.daPagare)}</strong>.</>}
              </p>
            )}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setNuovo(true)}><Icon name="plus" /> Nuovo agente</button>
      </header>

      {nuovo && <NuovoAgente onChiudi={() => setNuovo(false)} onCreato={(id) => { setNuovo(false); setScelto(id); qc.invalidateQueries({ queryKey: ['agents'] }); }} />}

      <div className="ag-cols">
        <nav className="ag-lista" aria-label="Agenti">
          {agenti.map((x) => (
            <button key={x.id} type="button" className={`ag-voce${a?.id === x.id ? ' is-on' : ''}${x.active ? '' : ' is-spento'}`} onClick={() => setScelto(x.id)}>
              <Avatar name={x.name} size={40} />
              <span className="ag-voce-tx">
                <span className="t">{x.name}</span>
                <span className="s">{pct(x.commissionPct)} sull'imponibile · {quanti(x.totali) || 'nessun lavoro'}</span>
              </span>
              <span className="ag-voce-cifra">
                {x.totali.daPagare > 0 ? <><span className="v">{euro(x.totali.daPagare)}</span><span className="l">da pagare</span></> : <span className="l">niente da pagare</span>}
              </span>
            </button>
          ))}
        </nav>
        <div className="ag-dettaglio">{a && <Dettaglio key={a.id} a={a} />}</div>
      </div>
    </div>
  );
}

function Frase({ a, tu }: { a: SchedaAgente; tu?: boolean }) {
  const t = a.totali;
  if (t.progetti + t.abbonamenti === 0) return <>{tu ? 'Non hai ancora lavori collegati.' : 'Nessun lavoro collegato ancora.'}</>;
  return (
    <>
      {tu ? 'Hai portato' : 'Ha portato'} <strong>{quanti(t)}</strong> per <strong>{euro(t.imponibile)}</strong> senza IVA.
      {' '}{tu ? 'La tua provvigione' : 'La provvigione'} è di <strong>{euro(t.provvigione)}</strong>: maturata {euro(t.maturata)} su quanto i clienti hanno già pagato,
      {' '}{parole(tu).pagata} {euro(t.pagata)}, <strong>{parole(tu).daPagare} {euro(t.daPagare)}</strong>.
    </>
  );
}

function Dettaglio({ a, soloLettura }: { a: SchedaAgente; soloLettura?: boolean }) {
  const [modifica, setModifica] = useState(false);
  return (
    <div className="ag-scheda">
      {!soloLettura && (
        <div className="ag-scheda-hd">
          <Avatar name={a.name} size={52} />
          <div className="min-w-0">
            <h2 className="ag-nome">{a.name}</h2>
            <p className="ag-sub">
              {pct(a.commissionPct)} sull'imponibile
              {a.email && <> · <a href={`mailto:${a.email}`}>{a.email}</a></>}
              {a.phone && <> · <a href={`tel:${a.phone.replace(/\s/g, '')}`}>{a.phone}</a></>}
            </p>
          </div>
          <button className="btn btn-sm" onClick={() => setModifica((v) => !v)}><Icon name="pencil" size={12} /> {modifica ? 'Chiudi' : 'Modifica'}</button>
        </div>
      )}
      {!soloLettura && modifica && <ModificaAgente a={a} onFatto={() => setModifica(false)} />}
      {!soloLettura && <p className="ag-frase"><Frase a={a} /></p>}

      <section className="ag-blocco">
        <div className="ag-blocco-hd">
          <h3 className="ag-h3"><Icon name="cube" size={14} /> {soloLettura ? 'I tuoi lavori' : 'I suoi lavori'}</h3>
          {(a.progetti.length > 0 || a.abbonamenti.length > 0) && <Legenda tu={soloLettura} />}
        </div>
        {a.progetti.length === 0 ? (
          <p className="ag-vuoto">{soloLettura ? 'Nessun lavoro ancora.' : "Nessun lavoro. Per collegarne uno apri il progetto, scheda Quanto ci resta, e scegli l'agente."}</p>
        ) : (
          <div className="ag-lavori">
            {a.progetti.map((p) => {
              const cliente = leggibile(p.client.shortName || p.client.legalName);
              const dentro = (
                <>
                  <BrandLogo name={cliente} size={34} />
                  <span className="ag-lavoro-chi">
                    <span className="t">{cliente}</span>
                    <span className="s">{nomeLavoro(p.name)}</span>
                  </span>
                  <span className="ag-lavoro-num">
                    <span className="v">{euro(p.provvigione.importo)}</span>
                    <span className="l">{pct(p.provvigione.pct)} di {euro(p.imponibile)}</span>
                  </span>
                  <span className="ag-lavoro-mat">
                    <BarraPagata totale={p.provvigione.importo} maturata={p.provvigione.maturata} pagata={p.provvigione.pagata} />
                    <span className="l"><Stato v={p.provvigione} tu={soloLettura} /></span>
                  </span>
                </>
              );
              return soloLettura
                ? <div key={p.id} className="ag-lavoro">{dentro}</div>
                : <Link key={p.id} to={`/projects/${p.id}?tab=conto`} className="ag-lavoro">{dentro}</Link>;
            })}
          </div>
        )}
      </section>

      {a.abbonamenti.length > 0 && <Abbonamenti a={a} tu={soloLettura} />}

      <Pagamenti a={a} soloLettura={soloLettura} />
      {!soloLettura && <Accesso a={a} />}
    </div>
  );
}

/**
 * Quanto della provvigione è pagato (nero), maturato e ancora da pagare (rosso), e quanto
 * matura solo quando il cliente paga (vuoto). Lorenzo, 27/10: la barra tutta nera di
 * "maturata" non diceva cosa era pagato e cosa no.
 */
function BarraPagata({ totale, maturata, pagata }: { totale: number; maturata: number; pagata: number }) {
  const p = Math.min(pagata, maturata);
  const w = (x: number) => `${totale > 0 ? Math.max(0, Math.min(100, (x / totale) * 100)) : 0}%`;
  return (
    <span className="ag-bar" aria-hidden>
      <i className="pagata" style={{ width: w(p) }} />
      <i className="dapagare" style={{ width: w(maturata - p) }} />
    </span>
  );
}

function Legenda({ tu }: { tu?: boolean }) {
  const w = parole(tu);
  return (
    <span className="ag-legenda" aria-hidden>
      <span><i className="pagata" />{w.pagata}</span>
      <span><i className="dapagare" />{w.daPagare}</span>
      <span><i />matura quando il cliente paga</span>
    </span>
  );
}

/** Come sta la provvigione di un lavoro, in parole: la cosa da fare (pagare) viene prima. */
function Stato({ v, tu }: { v: { importo: number; maturata: number; pagata: number; daPagare: number }; tu?: boolean }) {
  const w = parole(tu);
  const inPiu = v.pagata - v.maturata;
  if (v.daPagare > 0) return <><strong className="ag-rosso">{w.daPagare} {euro(v.daPagare)}</strong>{v.pagata > 0 && <> · {w.pagata} {euro(v.pagata)}</>}</>;
  if (inPiu >= 0.01) {
    return v.maturata >= v.importo
      ? <>{w.pagata} tutta, {euro(inPiu)} in più</>
      : <>{w.pagata} {euro(v.pagata)}, {euro(inPiu)} in anticipo</>;
  }
  if (v.maturata < v.importo - 0.009) return <>{v.pagata > 0 && <>{w.pagata} {euro(v.pagata)} · </>}il resto matura quando il cliente paga</>;
  return <>{w.pagata} tutta</>;
}

/** Gli abbonamenti dei suoi clienti: cosa pagano, quanto tocca a lui ogni mese, e il conto insieme. */
function Abbonamenti({ a, tu }: { a: SchedaAgente; tu?: boolean }) {
  const w = parole(tu);
  const t = a.abbonamentiTotali;
  const tuttiAbbonamenti = a.abbonamenti.every((x) => x.servizi.length > 0);
  return (
    <section className="ag-blocco">
      <h3 className="ag-h3"><Icon name="repeat" size={14} /> {tuttiAbbonamenti ? 'Abbonamenti' : 'Abbonamenti e altre fatture'}</h3>
      <p className="ag-spiega">
        I {w.suoi} clienti pagano un abbonamento: {w.lui} va il {pct(a.commissionPct)} di ogni pagamento, senza IVA
        {t.alMese > 0 ? <>, cioè <strong>{euro(t.alMese)} al mese</strong>.</> : '.'}
      </p>
      <div className="ag-lavori">
        {a.abbonamenti.map((x) => {
          const cliente = leggibile(x.client.shortName || x.client.legalName);
          const s = x.servizi[0];
          return (
            <div key={x.client.id} className="ag-lavoro">
              <BrandLogo name={s ? SERVIZIO[s.tipo] : cliente} size={34} />
              <span className="ag-lavoro-chi">
                <span className="t">{cliente}</span>
                <span className="s">{s ? <>{SERVIZIO[s.tipo]}{s.piano ? `, ${s.piano}` : ''}: {euro(s.importo)} {CICLO[s.ciclo]}</> : 'Fatture fuori dai lavori'}</span>
              </span>
              <span className="ag-lavoro-num">
                {x.alMese != null
                  ? <><span className="v">{euro(x.alMese)}</span><span className="l">{w.lui} ogni mese</span></>
                  : <><span className="v">{euro(x.provvigione)}</span><span className="l">{pct(a.commissionPct)} di {euro(x.fatturato)}</span></>}
              </span>
              <span className="ag-lavoro-mat">
                <span className="l">{x.fatture} {s ? (x.fatture === 1 ? 'rinnovo' : 'rinnovi') : (x.fatture === 1 ? 'fattura' : 'fatture')} da {mese(x.dal)}, maturati {euro(x.maturata)}</span>
              </span>
            </div>
          );
        })}
      </div>
      <div className="ag-abb-tot">
        <BarraPagata totale={t.maturata} maturata={t.maturata} pagata={t.pagata} />
        <span className="l">
          In tutto maturati {euro(t.maturata)}, {w.pagati} {euro(t.pagata)}
          {t.daPagare > 0 ? <>, <strong className="ag-rosso">{w.daPagare} {euro(t.daPagare)}</strong></> : ': niente ' + w.daPagare}.
        </span>
      </div>
    </section>
  );
}

function Pagamenti({ a, soloLettura }: { a: SchedaAgente; soloLettura?: boolean }) {
  const qc = useQueryClient();
  const [aperto, setAperto] = useState(false);
  const [f, setF] = useState({ importo: '', data: oggi(), progetto: '', nota: '' });
  useEffect(() => { setF((x) => ({ ...x, importo: a.totali.daPagare > 0 ? String(a.totali.daPagare).replace('.', ',') : '' })); }, [a.id, a.totali.daPagare]);
  const importo = Number(f.importo.replace(/\./g, '').replace(',', '.'));
  const valido = importo > 0 && !!f.data;

  const segna = useMutation({
    mutationFn: () => api.post(`/agents/${a.id}/pagamenti`, { amount: importo, paidAt: f.data, projectId: f.progetto || null, note: f.nota || null }),
    onSuccess: () => { setAperto(false); qc.invalidateQueries({ queryKey: ['agents'] }); qc.invalidateQueries({ queryKey: ['projects'] }); toast.ok('Pagamento segnato'); },
    onError: (e) => toast.alert('Non salvato', e instanceof ApiError ? e.message : 'Riprova tra qualche secondo.'),
  });
  const togli = useMutation({
    mutationFn: (id: string) => api.delete(`/agents/pagamenti/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['agents'] }); qc.invalidateQueries({ queryKey: ['projects'] }); toast.ok('Pagamento tolto'); },
    onError: (e) => toast.alert('Non tolto', e instanceof ApiError ? e.message : 'Riprova tra qualche secondo.'),
  });

  return (
    <section className="ag-blocco">
      <div className="ag-blocco-hd">
        <h3 className="ag-h3"><Icon name="banknote" size={14} /> {soloLettura ? 'Pagamenti ricevuti' : 'Provvigioni pagate'}</h3>
        {!soloLettura && !aperto && <button className="btn btn-sm" onClick={() => setAperto(true)}><Icon name="plus" size={12} /> Segna un pagamento</button>}
      </div>
      {a.pagamenti.length === 0 && !aperto && <p className="ag-vuoto">Nessun pagamento ancora.</p>}
      {a.pagamenti.length > 0 && (
        <div className="ag-pagamenti">
          {a.pagamenti.map((x) => (
            <div key={x.id} className="ag-pagamento">
              <span className="d">{new Date(x.paidAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
              <span className="p">{x.project ? x.project.name : 'Abbonamenti'}{x.note ? ` · ${x.note}` : ''}</span>
              <span className="v">{euro(x.amount)}</span>
              {!soloLettura && <button className="ag-x" aria-label="Togli questo pagamento" title="Togli questo pagamento" disabled={togli.isPending} onClick={() => togli.mutate(x.id)}><Icon name="x" size={12} /></button>}
            </div>
          ))}
        </div>
      )}
      {aperto && (
        <form className="ag-form" onSubmit={(e) => { e.preventDefault(); if (valido) segna.mutate(); }}>
          <label><span>Importo</span><input className="inp" inputMode="decimal" value={f.importo} onChange={(e) => setF({ ...f, importo: e.target.value })} /></label>
          <label><span>Pagato il</span><input className="inp" type="date" value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} /></label>
          <label className="lungo"><span>Per quale lavoro</span>
            <select className="inp" value={f.progetto} onChange={(e) => setF({ ...f, progetto: e.target.value })}>
              <option value="">Abbonamenti (o più lavori insieme)</option>
              {a.progetti.map((p) => <option key={p.id} value={p.id}>{(p.client.shortName || p.client.legalName)}: {p.name}</option>)}
            </select>
          </label>
          <label className="lungo"><span>Nota</span><input className="inp" placeholder="es. bonifico del 30/10" value={f.nota} onChange={(e) => setF({ ...f, nota: e.target.value })} /></label>
          <div className="ag-form-az">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAperto(false)}>Chiudi</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={!valido || segna.isPending}>Segna pagato</button>
          </div>
        </form>
      )}
    </section>
  );
}

function Accesso({ a }: { a: SchedaAgente }) {
  const qc = useQueryClient();
  const utentiQ = useQuery({
    queryKey: ['users', 'agenti'],
    queryFn: () => api.get<{ items: Array<{ id: string; email: string; firstName: string; lastName: string; role: { key: string | null } }> }>('/users?pageSize=100'),
    staleTime: 60_000,
  });
  const agentiUtenti = (utentiQ.data?.items ?? []).filter((u) => u.role.key === 'AGENTE');
  const collega = useMutation({
    mutationFn: (userId: string | null) => api.patch(`/agents/${a.id}`, { userId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['agents'] }); toast.ok('Accesso aggiornato'); },
    onError: (e) => toast.alert('Non salvato', e instanceof ApiError ? e.message : 'Riprova tra qualche secondo.'),
  });
  return (
    <section className="ag-blocco">
      <h3 className="ag-h3"><Icon name="lock" size={14} /> Accesso al gestionale</h3>
      {a.user ? (
        <p className="ag-vuoto">
          Entra con <strong>{a.user.email}</strong> e vede solo questa scheda: i suoi lavori e le sue provvigioni, mai margini, spese o tasse.{' '}
          <button className="ag-link" disabled={collega.isPending} onClick={() => collega.mutate(null)}>Togli l'accesso</button>
        </p>
      ) : (
        <>
          <p className="ag-vuoto">Non ha un accesso. Per dargliene uno crea un utente col ruolo <strong>Agente</strong> in Impostazioni, Utenti, poi collegalo qui.</p>
          {agentiUtenti.length > 0 && (
            <select className="inp ag-accesso" value="" disabled={collega.isPending} onChange={(e) => e.target.value && collega.mutate(e.target.value)} aria-label="Collega un accesso">
              <option value="">Collega un utente col ruolo Agente…</option>
              {agentiUtenti.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}, {u.email}</option>)}
            </select>
          )}
        </>
      )}
    </section>
  );
}

function ModificaAgente({ a, onFatto }: { a: SchedaAgente; onFatto: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: a.name, email: a.email ?? '', phone: a.phone ?? '', pct: String(a.commissionPct).replace('.', ',') });
  const p = Number(f.pct.replace(',', '.'));
  const salva = useMutation({
    mutationFn: () => api.patch(`/agents/${a.id}`, { name: f.name.trim(), email: f.email.trim() || null, phone: f.phone.trim() || null, commissionPct: p }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['agents'] }); qc.invalidateQueries({ queryKey: ['projects'] }); toast.ok('Agente aggiornato'); onFatto(); },
    onError: (e) => toast.alert('Non salvato', e instanceof ApiError ? e.message : 'Riprova tra qualche secondo.'),
  });
  return (
    <form className="ag-form ag-form--box" onSubmit={(e) => { e.preventDefault(); salva.mutate(); }}>
      <label className="lungo"><span>Nome e cognome</span><input className="inp" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
      <label><span>Email</span><input className="inp" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></label>
      <label><span>Telefono</span><input className="inp" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></label>
      <label><span>Provvigione sull'imponibile</span><span className="ag-pct"><input className="inp" inputMode="decimal" value={f.pct} onChange={(e) => setF({ ...f, pct: e.target.value })} /> %</span></label>
      <p className="ag-vuoto lungo">La percentuale vale per tutti i suoi lavori, tranne quelli dove è scritta una percentuale apposta.</p>
      <div className="ag-form-az">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onFatto}>Chiudi</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={f.name.trim().length < 2 || Number.isNaN(p) || p < 0 || p > 100 || salva.isPending}>Salva</button>
      </div>
    </form>
  );
}

function NuovoAgente({ onChiudi, onCreato }: { onChiudi: () => void; onCreato: (id: string) => void }) {
  const [f, setF] = useState({ name: '', email: '', phone: '', pct: '10' });
  const p = Number(f.pct.replace(',', '.'));
  const crea = useMutation({
    mutationFn: () => api.post<SchedaAgente>('/agents', { name: f.name.trim(), email: f.email.trim() || null, phone: f.phone.trim() || null, commissionPct: p }),
    onSuccess: (r) => { toast.ok('Agente aggiunto'); onCreato(r.id); },
    onError: (e) => toast.alert('Non salvato', e instanceof ApiError ? e.message : 'Riprova tra qualche secondo.'),
  });
  return (
    <form className="ag-form ag-form--box ag-nuovo" onSubmit={(e) => { e.preventDefault(); crea.mutate(); }}>
      <label className="lungo"><span>Nome e cognome</span><input className="inp" autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
      <label><span>Email</span><input className="inp" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></label>
      <label><span>Telefono</span><input className="inp" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></label>
      <label><span>Provvigione sull'imponibile</span><span className="ag-pct"><input className="inp" inputMode="decimal" value={f.pct} onChange={(e) => setF({ ...f, pct: e.target.value })} /> %</span></label>
      <div className="ag-form-az">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onChiudi}>Chiudi</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={f.name.trim().length < 2 || Number.isNaN(p) || p < 0 || p > 100 || crea.isPending}>Aggiungi l'agente</button>
      </div>
    </form>
  );
}
